/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ApplicationDetailedResponse } from '../models/application-detailed-response';
import { ApplicationResponse } from '../models/application-response';
import { AssignPrivilegesToApplicationRequest } from '../models/assign-privileges-to-application-request';
import { CreateApplicationRequest } from '../models/create-application-request';
import { GenericResponse } from '../models/generic-response';
import { GetApplicationDetailsResponse } from '../models/get-application-details-response';
import { GetApplicationsResponse } from '../models/get-applications-response';
import { UpdateApplicationRequest } from '../models/update-application-request';

@Injectable({ providedIn: 'root' })
export class ApplicationsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllApplications()` */
  static readonly GetAllApplicationsPath = '/api/v1/applications';

  /**
   * Get list of applications.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllApplications()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllApplications$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetApplicationsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.GetAllApplicationsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetApplicationsResponse>;
        })
      );
  }

  /**
   * Get list of applications.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllApplications$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllApplications(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetApplicationsResponse> {
    return this.getAllApplications$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetApplicationsResponse>
        ): GetApplicationsResponse => r.body
      )
    );
  }

  /** Path part for operation `createApplication()` */
  static readonly CreateApplicationPath = '/api/v1/applications';

  /**
   * Create a new application.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createApplication()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createApplication$Response(
    params: {
      body: CreateApplicationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApplicationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.CreateApplicationPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ApplicationResponse>;
        })
      );
  }

  /**
   * Create a new application.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createApplication$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createApplication(
    params: {
      body: CreateApplicationRequest;
    },
    context?: HttpContext
  ): Observable<ApplicationResponse> {
    return this.createApplication$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ApplicationResponse>): ApplicationResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getApplicationDetails()` */
  static readonly GetApplicationDetailsPath = '/api/v1/applications/{id}';

  /**
   * Get application's details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getApplicationDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getApplicationDetails$Response(
    params: {
      /**
       * The application's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApplicationDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.GetApplicationDetailsPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ApplicationDetailedResponse>;
        })
      );
  }

  /**
   * Get application's details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getApplicationDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getApplicationDetails(
    params: {
      /**
       * The application's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ApplicationDetailedResponse> {
    return this.getApplicationDetails$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ApplicationDetailedResponse>
        ): ApplicationDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `softDeleteApplicationById()` */
  static readonly SoftDeleteApplicationByIdPath = '/api/v1/applications/{id}';

  /**
   * Soft delete application using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteApplicationById()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteApplicationById$Response(
    params: {
      /**
       * The application's id to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.SoftDeleteApplicationByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Soft delete application using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `softDeleteApplicationById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteApplicationById(
    params: {
      /**
       * The application's id to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.softDeleteApplicationById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateApplication()` */
  static readonly UpdateApplicationPath = '/api/v1/applications/{id}';

  /**
   * Update application by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateApplication()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateApplication$Response(
    params: {
      /**
       * The application's id that needs to be updated
       */
      id: number;
      body: UpdateApplicationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApplicationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.UpdateApplicationPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ApplicationResponse>;
        })
      );
  }

  /**
   * Update application by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateApplication$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateApplication(
    params: {
      /**
       * The application's id that needs to be updated
       */
      id: number;
      body: UpdateApplicationRequest;
    },
    context?: HttpContext
  ): Observable<ApplicationResponse> {
    return this.updateApplication$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ApplicationResponse>): ApplicationResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `assignPrivilegesToApplication()` */
  static readonly AssignPrivilegesToApplicationPath =
    '/api/v1/applications/{application-id}/assign-privileges';

  /**
   * Assign privileges to application.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignPrivilegesToApplication()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignPrivilegesToApplication$Response(
    params: {
      /**
       * The application's id to which the privileges to be assigned
       */
      'application-id': number;
      body: AssignPrivilegesToApplicationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.AssignPrivilegesToApplicationPath,
      'post'
    );
    if (params) {
      rb.path('application-id', params['application-id'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Assign privileges to application.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignPrivilegesToApplication$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignPrivilegesToApplication(
    params: {
      /**
       * The application's id to which the privileges to be assigned
       */
      'application-id': number;
      body: AssignPrivilegesToApplicationRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.assignPrivilegesToApplication$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `getAllApplicationsWithDetails()` */
  static readonly GetAllApplicationsWithDetailsPath =
    '/api/v1/applications/details';

  /**
   * Get list of applications with features.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllApplicationsWithDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllApplicationsWithDetails$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetApplicationDetailsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ApplicationsService.GetAllApplicationsWithDetailsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetApplicationDetailsResponse>;
        })
      );
  }

  /**
   * Get list of applications with features.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllApplicationsWithDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllApplicationsWithDetails(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetApplicationDetailsResponse> {
    return this.getAllApplicationsWithDetails$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetApplicationDetailsResponse>
        ): GetApplicationDetailsResponse => r.body
      )
    );
  }
}
